/**
 * ResourceHistogramChart - Stacked bar chart visualization for Indirect Resources
 * PowerBI-style histogram with slicer/filter controls, rendered as a modal overlay.
 * Pure CSS/HTML chart (no external chart library).
 */

import React, { useState, useMemo, useEffect, useCallback, memo } from 'react';
import ReactDOM from 'react-dom';

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#6366f1', '#14b8a6', '#ef4444', '#ec4899', '#f97316', '#06b6d4', '#84cc16', '#a855f7'];

const GROUP_BY_OPTIONS = [
  { value: 'job_#', label: 'Job #' },
  { value: 'job_name', label: 'Job Name' },
  { value: 'discipline', label: 'Discipline' },
  { value: 'role', label: 'Role' },
  { value: 'role_discipline', label: 'Role-Discipline' },
  { value: 'division', label: 'Division' },
  { value: 'shop', label: 'Shop' },
  { value: 'empl_name', label: 'Employee Name' },
];

const SLICER_FIELDS = [
  { key: 'job_#', label: 'Job #' },
  { key: 'job_name', label: 'Job Name' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'role', label: 'Role' },
  { key: 'role_discipline', label: 'Role-Discipline' },
  { key: 'division', label: 'Division' },
  { key: 'shop', label: 'Shop' },
  { key: 'empl_name', label: 'Employee' },
];

function getRowWeekValue(row, weekKey) {
  if (row.weeks && typeof row.weeks === 'object') return row.weeks[weekKey];
  return row[weekKey];
}

function getRowField(row, field) {
  const val = row[field];
  if (val === null || val === undefined || val === '') return '(Blank)';
  return String(val);
}

const SlicerDropdown = memo(({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const s = search.trim().toLowerCase();
    return options.filter(o => o.toLowerCase().includes(s));
  }, [options, search]);

  const toggleValue = useCallback((val) => {
    onChange(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  }, [onChange]);

  const activeCount = selected.size;

  return (
    <div style={{ position: 'relative', minWidth: 130 }}>
      <button
        onClick={() => { setOpen(v => !v); setSearch(''); }}
        style={{
          width: '100%',
          padding: '6px 10px',
          fontSize: '0.8rem',
          border: `1px solid ${activeCount ? '#8b5cf6' : '#d1d5db'}`,
          borderRadius: 6,
          background: activeCount ? '#f5f3ff' : 'white',
          color: '#374151',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeCount ? `${label} (${activeCount})` : label}
        </span>
        <span style={{ fontSize: '0.65rem', opacity: 0.5 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <>
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10001 }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            width: 220,
            maxHeight: 260,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 10002,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '0.78rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            {activeCount > 0 && (
              <div
                style={{
                  padding: '4px 10px',
                  fontSize: '0.72rem',
                  color: '#8b5cf6',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f4f6',
                }}
                onClick={() => onChange(new Set())}
              >
                Clear all
              </div>
            )}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filtered.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: '0.78rem', color: '#9ca3af' }}>No matches</div>
              )}
              {filtered.map(opt => (
                <label
                  key={opt}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 10px',
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    background: selected.has(opt) ? '#f5f3ff' : 'transparent',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(opt)}
                    onChange={() => toggleValue(opt)}
                    style={{ accentColor: '#8b5cf6' }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const ResourceHistogramChart = memo(({ rows, weekColumns, isOpen, onClose, embedded = false }) => {
  const [groupBy, setGroupBy] = useState('discipline');
  const [slicers, setSlicers] = useState(() =>
    SLICER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: new Set() }), {})
  );
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [activeLegendItems, setActiveLegendItems] = useState(new Set());

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSlicers(SLICER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: new Set() }), {}));
      setActiveLegendItems(new Set());
    }
  }, [isOpen]);

  useEffect(() => {
    setActiveLegendItems(new Set());
  }, [groupBy]);

  const slicerOptions = useMemo(() => {
    const opts = {};
    SLICER_FIELDS.forEach(({ key }) => {
      const vals = new Set();
      (rows || []).forEach(r => vals.add(getRowField(r, key)));
      opts[key] = Array.from(vals).sort((a, b) => {
        if (a === '(Blank)') return 1;
        if (b === '(Blank)') return -1;
        return a.localeCompare(b, undefined, { numeric: true });
      });
    });
    return opts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows?.length) return [];
    return rows.filter(row =>
      SLICER_FIELDS.every(({ key }) => {
        const sel = slicers[key];
        if (!sel || sel.size === 0) return true;
        return sel.has(getRowField(row, key));
      })
    );
  }, [rows, slicers]);

  const { aggregated, groupKeys, maxTotal } = useMemo(() => {
    const weeks = weekColumns || [];
    const groupSet = new Set();
    const weekData = {};

    weeks.forEach(wc => {
      weekData[wc.key] = {};
    });

    filteredRows.forEach(row => {
      const grp = getRowField(row, groupBy);
      groupSet.add(grp);
      weeks.forEach(wc => {
        const val = parseFloat(getRowWeekValue(row, wc.key)) || 0;
        if (val === 0) return;
        if (!weekData[wc.key][grp]) weekData[wc.key][grp] = 0;
        weekData[wc.key][grp] += val;
      });
    });

    const groupKeys = Array.from(groupSet).sort((a, b) => {
      if (a === '(Blank)') return 1;
      if (b === '(Blank)') return -1;
      return a.localeCompare(b, undefined, { numeric: true });
    });

    let maxTotal = 0;
    weeks.forEach(wc => {
      let weekTotal = 0;
      groupKeys.forEach(gk => { weekTotal += (weekData[wc.key]?.[gk] || 0); });
      if (weekTotal > maxTotal) maxTotal = weekTotal;
    });

    return { aggregated: weekData, groupKeys, maxTotal };
  }, [filteredRows, groupBy, weekColumns]);

  const colorMap = useMemo(() => {
    const map = {};
    groupKeys.forEach((key, i) => { map[key] = COLORS[i % COLORS.length]; });
    return map;
  }, [groupKeys]);

  const yAxisTicks = useMemo(() => {
    if (maxTotal <= 0) return [0];
    const nice = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
    let step = 1;
    for (const n of nice) { if (maxTotal / n <= 8) { step = n; break; } }
    const ticks = [];
    for (let v = 0; v <= maxTotal + step * 0.5; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] < maxTotal) ticks.push(ticks[ticks.length - 1] + step);
    return ticks;
  }, [maxTotal]);

  const chartMaxY = yAxisTicks[yAxisTicks.length - 1] || 1;
  const weeks = weekColumns || [];
  const BAR_WIDTH = 38;
  const BAR_GAP = 6;
  const CHART_HEIGHT = 340;
  const Y_AXIS_WIDTH = 50;
  const chartContentWidth = weeks.length * (BAR_WIDTH + BAR_GAP) + BAR_GAP;

  const updateSlicer = useCallback((key) => (valOrFn) => {
    setSlicers(prev => ({
      ...prev,
      [key]: typeof valOrFn === 'function' ? valOrFn(prev[key]) : valOrFn,
    }));
  }, []);

  const clearAllSlicers = useCallback(() => {
    setSlicers(SLICER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: new Set() }), {}));
  }, []);

  const toggleLegendItem = useCallback((group) => {
    setActiveLegendItems(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      return next;
    });
  }, []);

  const hasActiveLegend = activeLegendItems.size > 0;

  const hasActiveSlicers = SLICER_FIELDS.some(f => slicers[f.key]?.size > 0);

  if (!isOpen && !embedded) return null;

  const innerBox = (
    <div
      onClick={e => { if (!embedded) e.stopPropagation(); }}
      style={{
        backgroundColor: 'white',
        borderRadius: 12,
        width: embedded ? '100%' : '92vw',
        maxWidth: embedded ? 'none' : 1200,
        height: embedded ? '100%' : undefined,
        maxHeight: embedded ? 'none' : '92vh',
        flex: embedded ? 1 : undefined,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: embedded ? 'none' : '0 20px 50px rgba(0,0,0,0.3)',
      }}
    >
        {/* Header */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f8f9fa',
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937', fontWeight: 600 }}>
            Resource Histogram
          </h3>
          {!embedded && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '0 4px',
                lineHeight: 1,
              }}
              title="Close"
            >
              ×
            </button>
          )}
        </div>

        {/* Toolbar: Slicers + Group By */}
        <div style={{
          padding: '10px 20px',
          borderBottom: '1px solid #f3f4f6',
          background: '#f8f9fa',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
        }}>
          {SLICER_FIELDS.map(f => (
            <SlicerDropdown
              key={f.key}
              label={f.label}
              options={slicerOptions[f.key] || []}
              selected={slicers[f.key] || new Set()}
              onChange={updateSlicer(f.key)}
            />
          ))}

          {hasActiveSlicers && (
            <button
              onClick={clearAllSlicers}
              style={{
                padding: '5px 10px',
                fontSize: '0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                background: 'white',
                color: '#ef4444',
                cursor: 'pointer',
              }}
            >
              Clear Filters
            </button>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>Group by:</span>
            <select
              value={groupBy}
              onChange={e => setGroupBy(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: '0.8rem',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {GROUP_BY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary bar */}
        <div style={{
          padding: '6px 20px',
          fontSize: '0.78rem',
          color: '#6b7280',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          gap: 20,
        }}>
          <span>{filteredRows.length} resource{filteredRows.length !== 1 ? 's' : ''} shown</span>
          <span>{weeks.length} week{weeks.length !== 1 ? 's' : ''}</span>
          <span>{groupKeys.length} {GROUP_BY_OPTIONS.find(o => o.value === groupBy)?.label || 'group'} group{groupKeys.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Chart area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 10px 20px' }}>
          {filteredRows.length === 0 ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: CHART_HEIGHT,
              color: '#9ca3af',
              fontSize: '0.95rem',
            }}>
              No data matching current filters
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex' }}>
                {/* Y axis */}
                <div style={{
                  width: Y_AXIS_WIDTH,
                  minWidth: Y_AXIS_WIDTH,
                  height: CHART_HEIGHT,
                  position: 'relative',
                  borderRight: '1px solid #e5e7eb',
                  flexShrink: 0,
                }}>
                  {yAxisTicks.map(tick => {
                    const pct = chartMaxY > 0 ? (tick / chartMaxY) * 100 : 0;
                    return (
                      <div
                        key={tick}
                        style={{
                          position: 'absolute',
                          bottom: `${pct}%`,
                          right: 6,
                          fontSize: '0.7rem',
                          color: '#9ca3af',
                          transform: 'translateY(50%)',
                          textAlign: 'right',
                          width: Y_AXIS_WIDTH - 10,
                        }}
                      >
                        {tick}
                      </div>
                    );
                  })}
                </div>

                {/* Scrollable chart */}
                <div style={{
                  flex: 1,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  position: 'relative',
                }}>
                  <div style={{
                    width: Math.max(chartContentWidth, 100),
                    height: CHART_HEIGHT,
                    position: 'relative',
                  }}>
                    {/* Grid lines */}
                    {yAxisTicks.map(tick => {
                      const pct = chartMaxY > 0 ? (tick / chartMaxY) * 100 : 0;
                      return (
                        <div
                          key={`grid-${tick}`}
                          style={{
                            position: 'absolute',
                            bottom: `${pct}%`,
                            left: 0,
                            right: 0,
                            height: 1,
                            background: tick === 0 ? '#d1d5db' : '#f3f4f6',
                          }}
                        />
                      );
                    })}

                    {/* Bars */}
                    {weeks.map((wc, wi) => {
                      const weekGroups = aggregated[wc.key] || {};
                      let cumulative = 0;
                      const barLeft = BAR_GAP + wi * (BAR_WIDTH + BAR_GAP);
                      const segments = groupKeys
                        .map(gk => {
                          const val = weekGroups[gk] || 0;
                          if (val <= 0) return null;
                          const segBottom = cumulative;
                          cumulative += val;
                          return { group: gk, value: val, bottom: segBottom };
                        })
                        .filter(Boolean);

                      const weekTotal = cumulative;
                      const totalTopPct = chartMaxY > 0 ? (weekTotal / chartMaxY) * 100 : 0;

                      return (
                        <div
                          key={wc.key}
                          style={{
                            position: 'absolute',
                            left: barLeft,
                            bottom: 0,
                            width: BAR_WIDTH,
                            height: '100%',
                          }}
                        >
                          {segments.map((seg) => {
                            const bottomPct = chartMaxY > 0 ? (seg.bottom / chartMaxY) * 100 : 0;
                            const heightPct = chartMaxY > 0 ? (seg.value / chartMaxY) * 100 : 0;
                            const isHovered = hoveredSegment?.week === wc.key && hoveredSegment?.group === seg.group;
                            const isActive = !hasActiveLegend || activeLegendItems.has(seg.group);
                            const displayVal = Math.round(seg.value * 100) / 100;
                            const segHeightPx = (heightPct / 100) * CHART_HEIGHT;
                            const showLabel = segHeightPx >= 16;
                            return (
                              <div
                                key={seg.group}
                                onMouseEnter={() => setHoveredSegment({ week: wc.key, weekLabel: wc.label, group: seg.group, value: seg.value })}
                                onMouseLeave={() => setHoveredSegment(null)}
                                onClick={() => toggleLegendItem(seg.group)}
                                style={{
                                  position: 'absolute',
                                  bottom: `${bottomPct}%`,
                                  left: 0,
                                  width: '100%',
                                  height: `${heightPct}%`,
                                  minHeight: heightPct > 0 ? 2 : 0,
                                  backgroundColor: colorMap[seg.group],
                                  opacity: isHovered ? 1 : (isActive ? 0.9 : 0.2),
                                  borderRadius: '2px 2px 0 0',
                                  transition: 'opacity 0.2s',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                }}
                                title={`${seg.group}: ${displayVal} — Week of ${wc.label} (click to highlight)`}
                              >
                                {showLabel && (
                                  <span style={{
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    color: '#fff',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                    lineHeight: 1,
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                  }}>
                                    {Number.isInteger(displayVal) ? displayVal : displayVal.toFixed(1)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {weekTotal > 0 && (
                            <div style={{
                              position: 'absolute',
                              bottom: `${totalTopPct}%`,
                              left: '50%',
                              transform: 'translate(-50%, -100%)',
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              color: '#374151',
                              whiteSpace: 'nowrap',
                              pointerEvents: 'none',
                              userSelect: 'none',
                              paddingBottom: 2,
                            }}>
                              {Number.isInteger(weekTotal) ? weekTotal : (Math.round(weekTotal * 100) / 100)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* X axis labels */}
                  <div style={{
                    display: 'flex',
                    width: Math.max(chartContentWidth, 100),
                    borderTop: '1px solid #d1d5db',
                    paddingTop: 4,
                  }}>
                    {weeks.map((wc, wi) => (
                      <div
                        key={wc.key}
                        style={{
                          width: BAR_WIDTH,
                          marginLeft: wi === 0 ? BAR_GAP : BAR_GAP,
                          fontSize: '0.62rem',
                          color: '#9ca3af',
                          textAlign: 'center',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {wc.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tooltip - fixed height to prevent layout shift/shaking */}
              <div style={{
                marginTop: 6,
                padding: '6px 14px',
                fontSize: '0.8rem',
                color: '#374151',
                background: '#f9fafb',
                borderRadius: 6,
                border: '1px solid #e5e7eb',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                height: 34,
                visibility: hoveredSegment ? 'visible' : 'hidden',
              }}>
                {hoveredSegment && (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      backgroundColor: colorMap[hoveredSegment.group],
                    }} />
                    <span style={{ fontWeight: 600 }}>{hoveredSegment.group}</span>
                    <span style={{ color: '#6b7280' }}>Week of {hoveredSegment.weekLabel}</span>
                    <span style={{ fontWeight: 600 }}>{Math.round(hoveredSegment.value * 100) / 100}</span>
                  </>
                )}
              </div>

              {/* Legend - click to highlight */}
              <div style={{
                marginTop: 14,
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px 16px',
                paddingBottom: 4,
                alignItems: 'center',
              }}>
                {groupKeys.map(gk => {
                  const isLegendActive = !hasActiveLegend || activeLegendItems.has(gk);
                  return (
                    <div
                      key={gk}
                      onClick={() => toggleLegendItem(gk)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        padding: '3px 8px',
                        borderRadius: 6,
                        border: activeLegendItems.has(gk) ? `2px solid ${colorMap[gk]}` : '2px solid transparent',
                        backgroundColor: activeLegendItems.has(gk) ? `${colorMap[gk]}15` : 'transparent',
                        opacity: isLegendActive ? 1 : 0.4,
                        transition: 'all 0.2s',
                        userSelect: 'none',
                      }}
                      title={`Click to ${activeLegendItems.has(gk) ? 'deselect' : 'highlight'} ${gk}`}
                    >
                      <span style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        backgroundColor: colorMap[gk],
                        flexShrink: 0,
                      }} />
                      <span style={{ fontSize: '0.76rem', color: '#4b5563', fontWeight: activeLegendItems.has(gk) ? 600 : 400 }}>{gk}</span>
                    </div>
                  );
                })}
                {hasActiveLegend && (
                  <div
                    onClick={() => setActiveLegendItems(new Set())}
                    style={{
                      fontSize: '0.72rem',
                      color: '#8b5cf6',
                      cursor: 'pointer',
                      padding: '3px 8px',
                      borderRadius: 6,
                      border: '1px solid #e5e7eb',
                      background: 'white',
                      userSelect: 'none',
                    }}
                  >
                    Clear selection
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
    </div>
  );

  if (embedded) {
    return innerBox;
  }

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
    >
      <div onClick={e => e.stopPropagation()}>
        {innerBox}
      </div>
    </div>,
    document.body
  );
});

export default ResourceHistogramChart;
